import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ExpertLayout from '../../components/ExpertLayout';
import { expertDashboardApi } from '../../lib/http';
import ClientDetailHeader from '../../components/expert/clients/ClientDetailHeader';
import ClientDetailStats from '../../components/expert/clients/ClientDetailStats';
import ClientDetailNotes from '../../components/expert/clients/ClientDetailNotes';
import ClientDetailBookings from '../../components/expert/clients/ClientDetailBookings';
import CreateBookingPanel from '../../components/expert/booking/CreateBookingPanel';
import type { ExpertClientDetail } from '../../types/client';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isBookingPanelOpen, setIsBookingPanelOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery<ExpertClientDetail>({
    queryKey: ['expert-client', id],
    queryFn: async () => {
      if (!id) throw new Error('No client ID');
      const response = await expertDashboardApi.expertClients.get(id);
      return response.data;
    },
    enabled: !!id,
  });

  const client = data;

  const bookingClient = client ? {
    id: client.id,
    name: client.effective_name,
    phone: client.effective_phone || '',
    email: client.effective_email || '',
  } : null;

  return (
    <ExpertLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/app/clients')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Clients
        </button>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-3" />
              <p className="text-sm text-gray-600">Loading client details...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-base font-medium text-red-800 mb-2">Client not found</p>
            <p className="text-sm text-red-600 mb-4">
              {(error as any)?.message || 'This client may have been removed.'}
            </p>
            <button
              onClick={() => navigate('/app/clients')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Back to Clients
            </button>
          </div>
        )}

        {/* Client content */}
        {client && (
          <div className="space-y-6">
            <ClientDetailHeader client={client} />
            <ClientDetailStats stats={client.booking_stats} />
            <ClientDetailNotes notes={client.private_notes} />
            <ClientDetailBookings bookings={client.recent_bookings} />

            {/* Create Booking button */}
            <div className="pb-20 md:pb-0">
              <button
                onClick={() => setIsBookingPanelOpen(true)}
                className="w-full py-3 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 transition-colors shadow-sm"
              >
                Create Booking
              </button>
            </div>
          </div>
        )}

        {/* Create Booking Panel */}
        <CreateBookingPanel
          isOpen={isBookingPanelOpen}
          onClose={() => setIsBookingPanelOpen(false)}
          onSuccess={() => {
            setIsBookingPanelOpen(false);
            refetch();
            queryClient.invalidateQueries({ queryKey: ['expert-clients'] });
          }}
          prefilledClient={bookingClient}
        />
      </div>
    </ExpertLayout>
  );
};

export default ClientDetail;
